import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon,
  QrCode as QrCodeIcon,
  VpnKey as VpnKeyIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as OperatorIcon,
  Visibility as ViewerIcon,
} from '@mui/icons-material';
import { appAuthAPI } from '../services/api';

const ROLE_CHOICES = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Administrator' },
];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && children}
    </div>
  );
}

function WebUIUsers() {
  const currentUser = useSelector((state) => state.auth.user);
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create User Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'viewer',
    is_active: true,
  });
  const [createLoading, setCreateLoading] = useState(false);

  // Edit User Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'viewer',
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Reset Password Dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // User Security Dialog (TOTP + WebAuthn)
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [securityUser, setSecurityUser] = useState(null);
  const [totpDevices, setTotpDevices] = useState([]);
  const [webauthnCredentials, setWebauthnCredentials] = useState([]);
  const [oidcLinks, setOidcLinks] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(false);

  // TOTP Setup
  const [totpSetupDialogOpen, setTotpSetupDialogOpen] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [totpDeviceName, setTotpDeviceName] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [totpSetupLoading, setTotpSetupLoading] = useState(false);

  // WebAuthn Setup
  const [webauthnSetupDialogOpen, setWebauthnSetupDialogOpen] = useState(false);
  const [webauthnCredentialName, setWebauthnCredentialName] = useState('');
  const [webauthnSetupLoading, setWebauthnSetupLoading] = useState(false);

  // Password visibility state
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showOidcSecret, setShowOidcSecret] = useState(false);

  // OIDC Providers
  const [oidcProviders, setOidcProviders] = useState([]);
  const [oidcProvidersLoading, setOidcProvidersLoading] = useState(false);
  const [oidcDialogOpen, setOidcDialogOpen] = useState(false);
  const [oidcFormData, setOidcFormData] = useState({
    name: '',
    client_id: '',
    client_secret: '',
    authorization_endpoint: '',
    token_endpoint: '',
    userinfo_endpoint: '',
    issuer: '',
  });
  const [oidcEditMode, setOidcEditMode] = useState(false);

  // Password Policy
  const [passwordPolicy, setPasswordPolicy] = useState({
    min_length: 8,
    max_length: 128,
    require_uppercase: true,
    require_lowercase: true,
    require_numbers: true,
    require_special_chars: true,
    special_chars: "!@#$%^&*()_+-=[]{}|;:',.<>?/`~",
    min_unique_chars: 4,
    disallow_common_passwords: true,
    disallow_username_in_password: true,
  });
  const [passwordPolicyLoading, setPasswordPolicyLoading] = useState(false);
  const [passwordPolicySaving, setPasswordPolicySaving] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState([]);

  // Password Test
  const [testPassword, setTestPassword] = useState('');
  const [testUsername, setTestUsername] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [showTestPassword, setShowTestPassword] = useState(false);

  useEffect(() => {
    loadUsers();
    if (activeTab === 1) {
      loadOIDCProviders();
    }
    if (activeTab === 2 && currentUser?.role === 'admin') {
      loadPasswordPolicy();
    }
  }, [activeTab, currentUser?.role]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await appAuthAPI.listUsers();
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadOIDCProviders = async () => {
    setOidcProvidersLoading(true);
    try {
      const response = await appAuthAPI.listOIDCProviders();
      setOidcProviders(response.data.providers || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load OIDC providers');
    } finally {
      setOidcProvidersLoading(false);
    }
  };

  const loadPasswordPolicy = async () => {
    setPasswordPolicyLoading(true);
    try {
      const response = await appAuthAPI.getPasswordPolicy();
      setPasswordPolicy(response.data.policy);
      setPasswordRequirements(response.data.requirements || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load password policy');
    } finally {
      setPasswordPolicyLoading(false);
    }
  };

  const handleSavePasswordPolicy = async () => {
    setPasswordPolicySaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await appAuthAPI.updatePasswordPolicy(passwordPolicy);
      setPasswordPolicy(response.data.policy);
      setPasswordRequirements(response.data.requirements || []);
      setSuccess('Password policy updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password policy');
    } finally {
      setPasswordPolicySaving(false);
    }
  };

  const handleTestPassword = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const response = await appAuthAPI.testPasswordPolicy(testPassword, testUsername);
      setTestResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to test password');
    } finally {
      setTestLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setCreateLoading(true);
    setError('');
    setSuccess('');
    try {
      await appAuthAPI.createUser(createFormData);
      setSuccess(`User ${createFormData.username} created successfully`);
      setCreateDialogOpen(false);
      setCreateFormData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'viewer',
        is_active: true,
      });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditUser = async () => {
    setEditLoading(true);
    setError('');
    setSuccess('');
    try {
      await appAuthAPI.updateUser(editUser.username, editFormData);
      setSuccess(`User ${editUser.username} updated successfully`);
      setEditDialogOpen(false);
      setEditUser(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleteLoading(true);
    setError('');
    setSuccess('');
    try {
      await appAuthAPI.deleteUser(deleteUser.username);
      setSuccess(`User ${deleteUser.username} deleted successfully`);
      setDeleteDialogOpen(false);
      setDeleteUser(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetPasswordLoading(true);
    setError('');
    setSuccess('');
    try {
      await appAuthAPI.adminResetPassword(resetPasswordUser.username, newPassword, sendEmail);
      setSuccess(`Password reset for user ${resetPasswordUser.username}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
      setSendEmail(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditFormData({
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || 'viewer',
      is_active: user.is_active ?? true,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setDeleteUser(user);
    setDeleteDialogOpen(true);
  };

  const openResetPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setResetPasswordDialogOpen(true);
  };

  const openSecurityDialog = async (user) => {
    setSecurityUser(user);
    setSecurityDialogOpen(true);
    setSecurityLoading(true);
    try {
      // Load TOTP devices for the specific user
      const totpResponse = await appAuthAPI.listUserTOTPDevices(user.username);
      setTotpDevices(totpResponse.data.devices || []);

      // Load WebAuthn credentials for the specific user
      const webauthnResponse = await appAuthAPI.listUserWebAuthnCredentials(user.username);
      setWebauthnCredentials(webauthnResponse.data.credentials || []);

      // Load OIDC links for the specific user
      const oidcResponse = await appAuthAPI.listUserOIDCLinks(user.username);
      setOidcLinks(oidcResponse.data.links || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load security settings');
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleTOTPSetup = async () => {
    setTotpSetupLoading(true);
    setError('');
    try {
      const response = await appAuthAPI.setupTOTP(totpDeviceName);
      setTotpSetupData(response.data);
      setSuccess('TOTP device setup initiated. Scan the QR code with your authenticator app.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup TOTP');
      setTotpSetupDialogOpen(false);
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const handleTOTPConfirm = async () => {
    setTotpSetupLoading(true);
    setError('');
    try {
      await appAuthAPI.confirmTOTP(totpSetupData.device_id, totpToken);
      setSuccess('TOTP device confirmed successfully');
      setTotpSetupDialogOpen(false);
      setTotpSetupData(null);
      setTotpDeviceName('');
      setTotpToken('');
      openSecurityDialog(securityUser);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm TOTP. Invalid token.');
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const handleTOTPDisable = async () => {
    if (!window.confirm(`Are you sure you want to disable TOTP authentication for ${securityUser.username}?`)) return;

    setSecurityLoading(true);
    setError('');
    try {
      // Admin endpoint to disable TOTP for specific user
      await appAuthAPI.disableUserTOTP(securityUser.username);
      setSuccess('TOTP disabled successfully');
      openSecurityDialog(securityUser);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable TOTP');
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleWebAuthnSetup = async () => {
    setWebauthnSetupLoading(true);
    setError('');
    try {
      // Step 1: Get registration options from backend
      // Don't specify authenticator_attachment to allow both platform and cross-platform
      const optionsResponse = await appAuthAPI.startWebAuthnRegistration(
        webauthnCredentialName,
        null // Allow any authenticator type (platform or security key)
      );

      const options = optionsResponse.data;
      console.log('WebAuthn options from backend:', options);
      console.log('Browser origin:', window.location.origin);
      console.log('Browser hostname:', window.location.hostname);
      console.log('RP ID from backend:', options.rp.id);

      // Validate response structure
      if (!options || !options.challenge || !options.user || !options.user.id) {
        throw new Error('Invalid registration options received from server');
      }

      // Helper function to convert base64url to ArrayBuffer
      const base64urlToBuffer = (base64url) => {
        if (!base64url) return new Uint8Array();
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const binary = atob(base64);
        return Uint8Array.from(binary, c => c.charCodeAt(0));
      };

      // Convert base64url strings to ArrayBuffer for WebAuthn API
      const publicKeyCredentialCreationOptions = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        user: {
          ...options.user,
          id: base64urlToBuffer(options.user.id),
        },
        excludeCredentials: options.excludeCredentials?.map(cred => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        })) || [],
      };

      // Step 2: Call WebAuthn API to create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (!credential) {
        throw new Error('No credential returned from browser');
      }

      // Step 3: Convert credential response to format expected by backend
      const credentialJSON = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))),
        },
        type: credential.type,
      };

      // Step 4: Send credential to backend for verification and storage
      console.log('Sending credential to backend for verification...');
      console.log('Credential JSON:', credentialJSON);

      try {
        const completeResponse = await appAuthAPI.completeWebAuthnRegistration(
          webauthnCredentialName,
          credentialJSON,
          options.challenge
        );
        console.log('Registration complete response:', completeResponse.data);
      } catch (verifyError) {
        console.error('Error during verification:', verifyError);
        console.error('Error response:', verifyError.response?.data);
        throw verifyError;
      }

      setSuccess('Passkey registered successfully!');
      setWebauthnSetupDialogOpen(false);
      setWebauthnCredentialName('');

      // Refresh security dialog to show new credential
      if (securityUser) {
        openSecurityDialog(securityUser);
      }
    } catch (err) {
      console.error('WebAuthn registration error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Registration was cancelled or timed out');
      } else if (err.name === 'NotSupportedError') {
        setError('WebAuthn is not supported by this browser');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to setup WebAuthn');
      }
    } finally {
      setWebauthnSetupLoading(false);
    }
  };

  const handleWebAuthnDelete = async (credentialId) => {
    if (!window.confirm(`Are you sure you want to delete this passkey for ${securityUser.username}?`)) return;

    setSecurityLoading(true);
    setError('');
    try {
      // Admin endpoint to delete WebAuthn credential for specific user
      await appAuthAPI.deleteUserWebAuthnCredential(securityUser.username, credentialId);
      setSuccess('Passkey deleted successfully');
      openSecurityDialog(securityUser);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete passkey');
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleOIDCUnlink = async (providerId) => {
    if (!window.confirm(`Are you sure you want to unlink this OIDC account for ${securityUser.username}?`)) return;

    setSecurityLoading(true);
    setError('');
    try {
      // Admin endpoint to unlink OIDC account for specific user
      await appAuthAPI.unlinkUserOIDCAccount(securityUser.username, providerId);
      setSuccess('OIDC account unlinked successfully');
      openSecurityDialog(securityUser);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlink OIDC account');
    } finally {
      setSecurityLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'operator':
        return 'warning';
      case 'viewer':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading && activeTab === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Authentication Management</Typography>
        <Box>
          {activeTab === 0 && (
            <>
              <Tooltip title="Refresh users list">
                <IconButton onClick={loadUsers} sx={{ mr: 1 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create User
              </Button>
            </>
          )}
          {activeTab === 1 && (
            <>
              <Tooltip title="Refresh OIDC providers">
                <IconButton onClick={loadOIDCProviders} sx={{ mr: 1 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setOidcEditMode(false);
                  setOidcFormData({
                    name: '',
                    client_id: '',
                    client_secret: '',
                    authorization_endpoint: '',
                    token_endpoint: '',
                    userinfo_endpoint: '',
                    issuer: '',
                  });
                  setOidcDialogOpen(true);
                }}
              >
                Add OIDC Provider
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Users & Authentication" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="OIDC Providers" icon={<VpnKeyIcon />} iconPosition="start" />
          {currentUser?.role === 'admin' && (
            <Tab label="Password Policy" icon={<LockIcon />} iconPosition="start" />
          )}
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>MFA Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.username}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      {user.first_name || user.last_name
                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || 'viewer'}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {user.totp_enabled && (
                          <Tooltip title="TOTP Enabled">
                            <Chip icon={<QrCodeIcon />} label="TOTP" size="small" color="success" />
                          </Tooltip>
                        )}
                        {user.webauthn_enabled && (
                          <Tooltip title="Passkey Enabled">
                            <Chip icon={<FingerprintIcon />} label="Passkey" size="small" color="success" />
                          </Tooltip>
                        )}
                        {!user.totp_enabled && !user.webauthn_enabled && (
                          <Chip label="None" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Security settings">
                        <IconButton
                          size="small"
                          onClick={() => openSecurityDialog(user)}
                          color="primary"
                        >
                          <SecurityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit user">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(user)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset password">
                        <IconButton
                          size="small"
                          onClick={() => openResetPasswordDialog(user)}
                          color="warning"
                        >
                          <LockIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete user">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(user)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {oidcProvidersLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : oidcProviders.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No OIDC providers configured. Click "Add OIDC Provider" to get started.
              </Typography>
            </Box>
          ) : (
            <Box p={2}>
              {oidcProviders.map((provider) => (
                <Card key={provider.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{provider.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Client ID: {provider.client_id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Issuer: {provider.issuer}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setOidcEditMode(true);
                        setOidcFormData(provider);
                        setOidcDialogOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        // Handle delete
                      }}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Password Policy Tab (Admin Only) */}
        {currentUser?.role === 'admin' && (
          <TabPanel value={activeTab} index={2}>
            {passwordPolicyLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Box p={2}>
                {/* Role Definitions Section */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="primary" />
                  Role Definitions
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <AdminIcon color="error" />
                        <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                          Administrator
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Full access to all features including:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Manage all WebUI users (create, edit, delete)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Configure password policies and security settings" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Manage OIDC providers and authentication methods" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Full read/write/delete access to Samba/LDAP resources (users, groups, OUs, GPOs)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Access to system diagnostics and service management" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Manage email configuration and SSH settings" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="View and manage audit logs" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>

                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <OperatorIcon color="warning" />
                        <Typography variant="subtitle1" fontWeight="bold" color="warning.main">
                          Operator
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Can manage Samba/LDAP resources but not application settings:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Full read/write/delete access to Samba/LDAP resources (users, groups, OUs, GPOs)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Reset domain user passwords" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Manage group memberships and organizational units" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Access to system diagnostics (read-only)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Cannot manage WebUI users, password policies, or application settings"
                            primaryTypographyProps={{ color: 'text.disabled' }}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ViewerIcon color="info" />
                        <Typography variant="subtitle1" fontWeight="bold" color="info.main">
                          Viewer
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Read-only access to the system:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="View Samba/LDAP resources (users, groups, OUs, GPOs)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="View system status and diagnostics" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Browse LDAP directory structure" />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Cannot create, modify, or delete any resources"
                            primaryTypographyProps={{ color: 'text.disabled' }}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Password Policy Configuration */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockIcon color="primary" />
                  Password Policy Configuration
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Configure the password requirements for all WebUI users. These settings apply when creating new users or changing passwords.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  {/* Length Requirements */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Length Requirements
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                          label="Minimum Length"
                          type="number"
                          value={passwordPolicy.min_length}
                          onChange={(e) => setPasswordPolicy({
                            ...passwordPolicy,
                            min_length: parseInt(e.target.value) || 6
                          })}
                          inputProps={{ min: 6, max: 128 }}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Maximum Length"
                          type="number"
                          value={passwordPolicy.max_length}
                          onChange={(e) => setPasswordPolicy({
                            ...passwordPolicy,
                            max_length: parseInt(e.target.value) || 128
                          })}
                          inputProps={{ min: 8, max: 256 }}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Minimum Unique Characters"
                          type="number"
                          value={passwordPolicy.min_unique_chars}
                          onChange={(e) => setPasswordPolicy({
                            ...passwordPolicy,
                            min_unique_chars: parseInt(e.target.value) || 1
                          })}
                          inputProps={{ min: 1, max: 20 }}
                          fullWidth
                          size="small"
                          helperText="Number of unique characters required"
                        />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Character Requirements */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Character Requirements
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.require_uppercase}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                require_uppercase: e.target.checked
                              })}
                            />
                          }
                          label="Require uppercase letter (A-Z)"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.require_lowercase}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                require_lowercase: e.target.checked
                              })}
                            />
                          }
                          label="Require lowercase letter (a-z)"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.require_numbers}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                require_numbers: e.target.checked
                              })}
                            />
                          }
                          label="Require number (0-9)"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.require_special_chars}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                require_special_chars: e.target.checked
                              })}
                            />
                          }
                          label="Require special character"
                        />
                        {passwordPolicy.require_special_chars && (
                          <TextField
                            label="Allowed Special Characters"
                            value={passwordPolicy.special_chars}
                            onChange={(e) => setPasswordPolicy({
                              ...passwordPolicy,
                              special_chars: e.target.value
                            })}
                            fullWidth
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Security Options */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Security Options
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.disallow_common_passwords}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                disallow_common_passwords: e.target.checked
                              })}
                            />
                          }
                          label="Block common passwords"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 6, mt: -1 }}>
                          Prevents use of commonly used passwords like "password123"
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordPolicy.disallow_username_in_password}
                              onChange={(e) => setPasswordPolicy({
                                ...passwordPolicy,
                                disallow_username_in_password: e.target.checked
                              })}
                            />
                          }
                          label="Disallow username in password"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 6, mt: -1 }}>
                          Prevents passwords that contain the user's username
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Current Requirements Preview */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Current Requirements Preview
                      </Typography>
                      <List dense>
                        {passwordRequirements.map((req, index) => (
                          <ListItem key={index}>
                            <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} />
                            <ListItemText primary={req} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={loadPasswordPolicy}
                    disabled={passwordPolicySaving}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSavePasswordPolicy}
                    disabled={passwordPolicySaving}
                  >
                    {passwordPolicySaving ? <CircularProgress size={24} /> : 'Save Policy'}
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Password Test Tool */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="primary" />
                  Password Policy Tester
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Test a password against the current policy to see if it meets all requirements.
                </Typography>

                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Password to Test"
                        type={showTestPassword ? 'text' : 'password'}
                        value={testPassword}
                        onChange={(e) => setTestPassword(e.target.value)}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowTestPassword(!showTestPassword)}
                                edge="end"
                              >
                                {showTestPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Username (optional)"
                        value={testUsername}
                        onChange={(e) => setTestUsername(e.target.value)}
                        fullWidth
                        helperText="Enter a username to test the 'username in password' rule"
                      />
                      <Button
                        variant="contained"
                        onClick={handleTestPassword}
                        disabled={testLoading || !testPassword}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {testLoading ? <CircularProgress size={24} /> : 'Test Password'}
                      </Button>

                      {testResult && (
                        <Box sx={{ mt: 2 }}>
                          <Alert
                            severity={testResult.valid ? 'success' : 'error'}
                            icon={testResult.valid ? <CheckCircleIcon /> : <CancelIcon />}
                          >
                            {testResult.valid
                              ? 'Password meets all policy requirements!'
                              : 'Password does not meet policy requirements'}
                          </Alert>
                          {!testResult.valid && testResult.errors && testResult.errors.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" color="error" gutterBottom>
                                Issues found:
                              </Typography>
                              <List dense>
                                {testResult.errors.map((error, index) => (
                                  <ListItem key={index}>
                                    <CancelIcon color="error" sx={{ mr: 1, fontSize: 18 }} />
                                    <ListItemText primary={error} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>
        )}
      </Paper>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !createLoading && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              value={createFormData.username}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, username: e.target.value })
              }
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={createFormData.email}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, email: e.target.value })
              }
              required
              fullWidth
            />
            <TextField
              label="Password"
              type={showCreatePassword ? 'text' : 'password'}
              value={createFormData.password}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, password: e.target.value })
              }
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      edge="end"
                    >
                      {showCreatePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="First Name"
              value={createFormData.first_name}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, first_name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Last Name"
              value={createFormData.last_name}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, last_name: e.target.value })
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={createFormData.role}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, role: e.target.value })
                }
                label="Role"
              >
                {ROLE_CHOICES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={createFormData.is_active}
                  onChange={(e) =>
                    setCreateFormData({ ...createFormData, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={
              createLoading ||
              !createFormData.username ||
              !createFormData.email ||
              !createFormData.password
            }
          >
            {createLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editLoading && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User: {editUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) =>
                setEditFormData({ ...editFormData, email: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="First Name"
              value={editFormData.first_name}
              onChange={(e) =>
                setEditFormData({ ...editFormData, first_name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Last Name"
              value={editFormData.last_name}
              onChange={(e) =>
                setEditFormData({ ...editFormData, last_name: e.target.value })
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editFormData.role}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, role: e.target.value })
                }
                label="Role"
              >
                {ROLE_CHOICES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditUser}
            variant="contained"
            disabled={editLoading || !editFormData.email}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{deleteUser?.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => !resetPasswordLoading && setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password: {resetPasswordUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Password"
              type={showResetPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
              }
              label="Send password reset email to user"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResetPasswordDialogOpen(false)}
            disabled={resetPasswordLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={resetPasswordLoading || !newPassword}
          >
            {resetPasswordLoading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Settings Dialog */}
      <Dialog
        open={securityDialogOpen}
        onClose={() => !securityLoading && setSecurityDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Security Settings: {securityUser?.username}
        </DialogTitle>
        <DialogContent>
          {securityLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              {/* TOTP Section */}
              <Typography variant="h6" gutterBottom>
                <QrCodeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Two-Factor Authentication (TOTP)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {totpDevices.length === 0 ? (
                <Box mb={3}>
                  {securityUser?.username === currentUser?.username ? (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        No TOTP devices configured. Add a device to enable two-factor authentication.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<QrCodeIcon />}
                        onClick={() => setTotpSetupDialogOpen(true)}
                      >
                        Enable TOTP
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        To enable TOTP for {securityUser?.username}, they must log in to their own account and configure it from their profile settings. Admins cannot set up TOTP for other users as this requires access to their personal authenticator device.
                      </Alert>
                      <Typography variant="body2" color="textSecondary">
                        No TOTP devices configured for this user.
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                <Box mb={3}>
                  <List>
                    {totpDevices.map((device) => (
                      <ListItem key={device.id}>
                        <ListItemText
                          primary={device.name}
                          secondary={`Added: ${new Date(device.created_at).toLocaleString()}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            color="error"
                            onClick={handleTOTPDisable}
                          >
                            Disable
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* WebAuthn Section */}
              <Typography variant="h6" gutterBottom>
                <FingerprintIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Passkeys / Security Keys (WebAuthn)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {webauthnCredentials.length === 0 ? (
                <Box mb={3}>
                  {securityUser?.username === currentUser?.username ? (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        No passkeys configured. Add a passkey for passwordless authentication.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<FingerprintIcon />}
                        onClick={() => setWebauthnSetupDialogOpen(true)}
                      >
                        Add Passkey
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        To add a passkey for {securityUser?.username}, they must log in to their own account and register it from their profile settings. Admins cannot register passkeys for other users as this requires access to their personal authentication device.
                      </Alert>
                      <Typography variant="body2" color="textSecondary">
                        No passkeys configured for this user.
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                <Box mb={3}>
                  <List>
                    {webauthnCredentials.map((cred) => (
                      <ListItem key={cred.id}>
                        <ListItemText
                          primary={cred.name}
                          secondary={`Added: ${new Date(cred.created_at).toLocaleString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleWebAuthnDelete(cred.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    To add more passkeys for {securityUser?.username}, they must log in to their own account.
                  </Alert>
                </Box>
              )}

              {/* OIDC Links Section */}
              <Typography variant="h6" gutterBottom>
                <LinkIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Linked OIDC Accounts
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {oidcLinks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No OIDC accounts linked
                </Typography>
              ) : (
                <List>
                  {oidcLinks.map((link) => (
                    <ListItem key={link.provider_id}>
                      <ListItemText
                        primary={link.provider_name}
                        secondary={`Linked: ${new Date(link.linked_at).toLocaleString()}`}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          color="error"
                          onClick={() => handleOIDCUnlink(link.provider_id)}
                        >
                          Unlink
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* TOTP Setup Dialog */}
      <Dialog
        open={totpSetupDialogOpen}
        onClose={() => !totpSetupLoading && setTotpSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!totpSetupData ? (
              <>
                <TextField
                  label="Device Name"
                  value={totpDeviceName}
                  onChange={(e) => setTotpDeviceName(e.target.value)}
                  placeholder="e.g., My Phone"
                  fullWidth
                  autoFocus
                />
                <Typography variant="body2" color="textSecondary">
                  Choose a name to identify this device (e.g., "My iPhone", "Work Phone")
                </Typography>
              </>
            ) : (
              <>
                <Alert severity="info">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </Alert>
                <Box display="flex" justifyContent="center" my={2}>
                  <img src={totpSetupData.qr_code} alt="TOTP QR Code" style={{ maxWidth: '300px' }} />
                </Box>
                <TextField
                  label="Verification Code"
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value)}
                  placeholder="Enter 6-digit code"
                  fullWidth
                  autoFocus
                />
                <Typography variant="caption" color="textSecondary">
                  Secret key (manual entry): {totpSetupData.secret}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTotpSetupDialogOpen(false);
              setTotpSetupData(null);
              setTotpDeviceName('');
              setTotpToken('');
            }}
            disabled={totpSetupLoading}
          >
            Cancel
          </Button>
          {!totpSetupData ? (
            <Button
              onClick={handleTOTPSetup}
              variant="contained"
              disabled={totpSetupLoading || !totpDeviceName}
            >
              {totpSetupLoading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          ) : (
            <Button
              onClick={handleTOTPConfirm}
              variant="contained"
              disabled={totpSetupLoading || !totpToken}
            >
              {totpSetupLoading ? <CircularProgress size={24} /> : 'Verify & Enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* WebAuthn Setup Dialog */}
      <Dialog
        open={webauthnSetupDialogOpen}
        onClose={() => !webauthnSetupLoading && setWebauthnSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Passkey / Security Key</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Credential Name"
              value={webauthnCredentialName}
              onChange={(e) => setWebauthnCredentialName(e.target.value)}
              placeholder="e.g., My Yubikey, TouchID"
              fullWidth
              autoFocus
            />
            <Alert severity="info">
              After clicking "Register", follow your browser's prompts to register your passkey or security key.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWebauthnSetupDialogOpen(false);
              setWebauthnCredentialName('');
            }}
            disabled={webauthnSetupLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleWebAuthnSetup}
            variant="contained"
            disabled={webauthnSetupLoading || !webauthnCredentialName}
          >
            {webauthnSetupLoading ? <CircularProgress size={24} /> : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OIDC Provider Dialog */}
      <Dialog
        open={oidcDialogOpen}
        onClose={() => setOidcDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {oidcEditMode ? 'Edit OIDC Provider' : 'Add OIDC Provider'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Provider Name"
              value={oidcFormData.name}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, name: e.target.value })
              }
              placeholder="e.g., Google, Okta, Azure AD"
              fullWidth
            />
            <TextField
              label="Client ID"
              value={oidcFormData.client_id}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, client_id: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Client Secret"
              type={showOidcSecret ? 'text' : 'password'}
              value={oidcFormData.client_secret}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, client_secret: e.target.value })
              }
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle secret visibility"
                      onClick={() => setShowOidcSecret(!showOidcSecret)}
                      edge="end"
                    >
                      {showOidcSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Issuer URL"
              value={oidcFormData.issuer}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, issuer: e.target.value })
              }
              placeholder="https://accounts.google.com"
              fullWidth
            />
            <TextField
              label="Authorization Endpoint"
              value={oidcFormData.authorization_endpoint}
              onChange={(e) =>
                setOidcFormData({
                  ...oidcFormData,
                  authorization_endpoint: e.target.value,
                })
              }
              fullWidth
            />
            <TextField
              label="Token Endpoint"
              value={oidcFormData.token_endpoint}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, token_endpoint: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Userinfo Endpoint"
              value={oidcFormData.userinfo_endpoint}
              onChange={(e) =>
                setOidcFormData({ ...oidcFormData, userinfo_endpoint: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOidcDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              // Handle save
              setOidcDialogOpen(false);
            }}
          >
            {oidcEditMode ? 'Update' : 'Add Provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WebUIUsers;
